import { AsyncActionMethods } from '../types/ActionMethods';
import { capitalize } from '../../lib/string/lib';
import { HttpVerb } from '@sonar-software/sonar-http';
import {
	AsyncActionTypeKey,
	AsyncActionTypes,
	AsyncActionTypesMap,
	AsyncActionTypeValue,
	AsyncActionVerbTypeOptions
} from './types';
import { AsyncActionTypeNameOptions, AsyncActionTypeOptions } from '../types/Options';

export function createAsyncActionTypeKey<AM extends AsyncActionMethods, HV extends HttpVerb, N extends string>(
	options: AsyncActionTypeNameOptions<AM, HV, N>
): AsyncActionTypeKey<AM, HV, N> {
	const { actionMethod, httpVerb, name } = options;

	return `${actionMethod}_${httpVerb.toLowerCase()}_${name}`.toUpperCase() as AsyncActionTypeKey<AM, HV, N>;
}

export function createAsyncActionTypeValue<AM extends AsyncActionMethods, HV extends HttpVerb, N extends string>(
	options: AsyncActionTypeNameOptions<AM, HV, N>
): AsyncActionTypeValue<AM, HV, N> {
	const { actionMethod, httpVerb, name } = options;
	const actionMethodLowercase = actionMethod.toLowerCase();
	const httpVerbCapialized = capitalize(httpVerb.toLowerCase());
	const nameCapitalized = capitalize(name);

	return `${actionMethodLowercase}${httpVerbCapialized}${nameCapitalized}` as AsyncActionTypeValue<AM, HV, N>;
}

export function createAsyncActionTypesMap<HV extends HttpVerb, N extends string>(
	options: AsyncActionTypeOptions<HV, N>
): AsyncActionTypesMap<HV, N> {
	return {
		[ AsyncActionMethods.Error ]: createAsyncActionTypeValue({
			actionMethod: AsyncActionMethods.Error,
			...options
		}),
		[ AsyncActionMethods.Failed ]: createAsyncActionTypeValue({
			actionMethod: AsyncActionMethods.Failed,
			...options
		}),
		[ AsyncActionMethods.Receive ]: createAsyncActionTypeValue({
			actionMethod: AsyncActionMethods.Receive,
			...options
		}),
		[ AsyncActionMethods.Repeat ]: createAsyncActionTypeValue({
			actionMethod: AsyncActionMethods.Repeat,
			...options
		}),
		[ AsyncActionMethods.Request ]: createAsyncActionTypeValue({
			actionMethod: AsyncActionMethods.Request,
			...options
		}),
		[ AsyncActionMethods.Reset ]: createAsyncActionTypeValue({
			actionMethod: AsyncActionMethods.Reset,
			...options
		})
	};
}

export function createAsyncActionTypes<HV extends HttpVerb, N extends string>(
	options: AsyncActionTypeOptions<HV, N>
): AsyncActionTypes<HV, N, AsyncActionTypesMap<HV, N>> {
	const map = createAsyncActionTypesMap(options);

	return {
		[ createAsyncActionTypeKey({
			...options,
			actionMethod: AsyncActionMethods.Error
		}) ]: map.error,
		[ createAsyncActionTypeKey({
			actionMethod: AsyncActionMethods.Failed,
			...options
		}) ]: map.failed,
		[ createAsyncActionTypeKey({
			actionMethod: AsyncActionMethods.Receive,
			...options
		}) ]: map.receive,
		[ createAsyncActionTypeKey({
			actionMethod: AsyncActionMethods.Request,
			...options
		}) ]: map.request,
		[ createAsyncActionTypeKey({
			actionMethod: AsyncActionMethods.Reset,
			...options
		}) ]: map.reset
	} as AsyncActionTypes<HV, N, AsyncActionTypesMap<HV, N>>;
}

export function createDeleteActionTypes<N extends string>(
	options: AsyncActionVerbTypeOptions<N>
): AsyncActionTypes<HttpVerb.Delete, N, AsyncActionTypesMap<HttpVerb.Delete, N>> {
	return createAsyncActionTypes({
		httpVerb: HttpVerb.Delete,
		...options
	});
}

export function createGetActionTypes<N extends string>(
	options: AsyncActionVerbTypeOptions<N>
): AsyncActionTypes<HttpVerb.Get, N, AsyncActionTypesMap<HttpVerb.Get, N>> {
	return createAsyncActionTypes({
		httpVerb: HttpVerb.Get,
		...options
	});
}

export function createPatchActionTypes<N extends string>(
	options: AsyncActionVerbTypeOptions<N>
): AsyncActionTypes<HttpVerb.Patch, N, AsyncActionTypesMap<HttpVerb.Patch, N>> {
	return createAsyncActionTypes({
		httpVerb: HttpVerb.Patch,
		...options
	});
}

export function createPostActionTypes<N extends string>(
	options: AsyncActionVerbTypeOptions<N>
): AsyncActionTypes<HttpVerb.Post, N, AsyncActionTypesMap<HttpVerb.Post, N>> {
	return createAsyncActionTypes({
		httpVerb: HttpVerb.Post,
		...options
	});
}

export function createPutActionTypes<N extends string>(
	options: AsyncActionVerbTypeOptions<N>
): AsyncActionTypes<HttpVerb.Put, N, AsyncActionTypesMap<HttpVerb.Put, N>> {
	return createAsyncActionTypes({
		httpVerb: HttpVerb.Put,
		...options
	});
}
